import { Flex, Box } from "@chakra-ui/react";
import { LatLong } from "features/maps/typings";
import { MdGpsFixed } from "react-icons/md";
import { FiShare2, FiDownload } from "react-icons/fi";
import { useControls } from "../../hooks/useControls";
import { showError } from "infra/helpers/alerta";
import { useFilters } from "features/filters/store";
import { useFiltersLists } from "features/filters/store/lists";
import { useSaveShare } from "features/share-map/hooks/useSaveShare";
import { useCallback, useState } from "react";
import { Spin, Modal } from "antd";
import CopyToClipboard from "@vigosan/react-copy-to-clipboard";
import { TipoMapaTalhoes } from "features/maps/typings";
import ControlItem from "./ControlItem";
import ModalCopy from "./ModalCopy";
import MenuTypeMap from "./MenuTypeMap";
import ControlZoom from "./ControlZoom";
import { exportFilters } from "features/filters/services/export-filters";
import { useMapaDeChuvaPeriodos } from "components/inputs/filter-safra-unidade-mapatematico/items/mapa-de-chuva/hooks/useMapaDeChuvaPeriodos";
import { getDateStringFromDate } from "../advancedFilters/items/utils/mapa-de-chuva.utils";
import { useMapaDeChuvaActualIndiceDates } from "components/inputs/filter-safra-unidade-mapatematico/items/mapa-de-chuva/hooks/MapaDeChuvaActualIndiceDates";
import ControlViewType from "./ControlViewType";
import { CurrentViewTypeEnum, PointsBaseMap } from "../../typings";
import { exportRainfallMapPng } from "core/features/maps/rainfall-interpolation/exportRainfallMapPng";

function isChuvasTipoMapa(tipoMapa: TipoMapaTalhoes | number | string) {
  return Number(tipoMapa) === TipoMapaTalhoes.Chuvas;
}

function MapControl({
  map,
  center,
  currentViewType,
  handleChangeCurrentViewType,
  points,
  enableRainfallPngExport,
}: {
  map: google.maps.Map;
  center: LatLong;
  currentViewType: CurrentViewTypeEnum;
  handleChangeCurrentViewType: (prop: CurrentViewTypeEnum) => void;
  points?: PointsBaseMap;
  enableRainfallPngExport?: boolean;
}) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [urlShared, setUrlShared] = useState<string | null>(null);
  const [isExportingPng, setIsExportingPng] = useState(false);
  const { value: mapaDeChuvaIndicesValues } = useMapaDeChuvaActualIndiceDates();

  const { resetNavigate } = useControls(map);
  const { filters } = useFilters();
  const { mapaDeChuvas } = useFiltersLists();
  const { mutate: _handleSave, isLoading } = useSaveShare();

  const { tipoMapa } = filters;

  const { value, items } = useMapaDeChuvaPeriodos();

  const mapaDeChuvaFiltroRapidoFounded = useCallback(() => {
    return items.find((item) => item.codigo === value);
  }, [items, value]);

  const mapaDeChuvaPeriodoDataInicial = getDateStringFromDate(
    new Date(mapaDeChuvaFiltroRapidoFounded()?.dataInicial!)
  );
  const mapaDeChuvaPeriodoDataFinal = getDateStringFromDate(
    new Date(mapaDeChuvaFiltroRapidoFounded()?.dataFinal!)
  );

  const acutalPeriodoSelected = useCallback(
    () =>
      mapaDeChuvaFiltroRapidoFounded()
        ? {
            horaInicial: mapaDeChuvaIndicesValues.initialHour,
            horaFinal: mapaDeChuvaIndicesValues.endHour,
            dataInicial: mapaDeChuvaPeriodoDataInicial,
            dataFinal: mapaDeChuvaPeriodoDataFinal,
          }
        : {
            horaInicial: filters.filtroMapaDeChuva?.horaInicial!,
            horaFinal: filters.filtroMapaDeChuva?.horaFinal!,
            dataInicial: filters.filtroMapaDeChuva?.dataInicial,
            dataFinal: filters.filtroMapaDeChuva?.dataFinal,
          },
    [
      mapaDeChuvaFiltroRapidoFounded,
      mapaDeChuvaIndicesValues.initialHour,
      mapaDeChuvaIndicesValues.endHour,
      mapaDeChuvaPeriodoDataInicial,
      mapaDeChuvaPeriodoDataFinal,
      filters.filtroMapaDeChuva?.dataFinal,
      filters.filtroMapaDeChuva?.dataInicial,
      filters.filtroMapaDeChuva?.horaInicial,
      filters.filtroMapaDeChuva?.horaFinal,
    ]
  );

  const showShareButton = tipoMapa !== TipoMapaTalhoes.Satelite;
  const showDownloadPngButton =
    enableRainfallPngExport ?? isChuvasTipoMapa(tipoMapa);

  const handleDownloadPng = useCallback(async () => {
    if (isExportingPng) {
      return;
    }

    if (!points?.features?.length) {
      showError("Não há dados do mapa de chuva para exportar.");
      return;
    }

    const selectedBandCodes =
      filters.filtroMapaDeChuva?.indicesPluviometrico ?? [];
    const periodo = acutalPeriodoSelected();

    setIsExportingPng(true);
    try {
      await exportRainfallMapPng({
        points,
        indices: mapaDeChuvas?.indices,
        selectedBandCodes,
        fazendaNome: filters.unidade?.nome,
        periodo: {
          dataInicial: periodo.dataInicial,
          dataFinal: periodo.dataFinal,
          horaInicial: periodo.horaInicial,
          horaFinal: periodo.horaFinal,
        },
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Não foi possível gerar a imagem do mapa.";
      showError(message);
    } finally {
      setIsExportingPng(false);
    }
  }, [
    isExportingPng,
    points,
    filters.filtroMapaDeChuva?.indicesPluviometrico,
    filters.unidade?.nome,
    mapaDeChuvas?.indices,
    acutalPeriodoSelected,
  ]);

  const handleShare = useCallback(() => {
    if (isLoading) {
      return;
    }

    const data = exportFilters({
      mapaDeChuva: {
        dataInicial: acutalPeriodoSelected().dataInicial,
        dataFinal: acutalPeriodoSelected().dataFinal,
        horaFinal: acutalPeriodoSelected().horaFinal,
        horaInicial: acutalPeriodoSelected().horaInicial,
        indicesPluviometrico: filters.filtroMapaDeChuva?.indicesPluviometrico,
        periodo: 6,
      },
    });

    _handleSave(
      {
        data: data,
        feature: `${data.tipoMapa}`,
      },
      {
        onSuccess: (res) => {
          const url = `${window.location.origin}/s/${res.code}`;

          setUrlShared(url);

          setIsModalVisible(true);
        },
        onError: () => showError(),
      }
    );
  }, [
    _handleSave,
    isLoading,
    filters.filtroMapaDeChuva,
    acutalPeriodoSelected,
  ]);

  return (
    <CopyToClipboard
      render={({ copy }: any) => (
        <Flex
          pos="fixed"
          flexDir="column"
          gridGap="12px"
          right="16px"
          bottom="32px"
          zIndex={9}
        >
          <Modal
            visible={isExportingPng}
            footer={null}
            closable={false}
            maskClosable={false}
            keyboard={false}
            centered
            zIndex={10000}
            bodyStyle={{ padding: "28px 32px" }}
          >
            <Flex alignItems="center" gridGap="16px">
              <Spin size="large" />
              <Box>
                <Box fontWeight="bold" fontSize="16px" color="gray.700">
                  Gerando imagem do mapa
                </Box>
                <Box fontSize="14px" color="gray.500" marginTop="4px">
                  Aguarde. A navegação no mapa fica bloqueada durante a
                  exportação.
                </Box>
              </Box>
            </Flex>
          </Modal>

          {isModalVisible ? (
            <ModalCopy
              onClose={() => setIsModalVisible(false)}
              visible={isModalVisible}
              url={urlShared!}
              onOk={() => {
                copy(urlShared);
                setIsModalVisible(false);
              }}
            />
          ) : null}

          {showDownloadPngButton ? (
            <ControlItem
              testId="config-mapa-download-png"
              onClick={() => {
                void handleDownloadPng();
              }}
            >
              {isExportingPng ? (
                <Box pt="4px">
                  <Spin size="small" />
                </Box>
              ) : (
                <FiDownload fontSize="18px" title="Baixar PNG do mapa" />
              )}
            </ControlItem>
          ) : null}

          {showShareButton ? (
            <ControlItem
              testId="config-mapa-compartilhar"
              onClick={() => handleShare()}
            >
              {isLoading ? (
                <Box pt="4px">
                  <Spin />
                </Box>
              ) : (
                <FiShare2 fontSize="18px" />
              )}
            </ControlItem>
          ) : null}

          <ControlViewType
            currentType={currentViewType}
            handleChange={handleChangeCurrentViewType}
          />

          <ControlItem
            testId="config-mapa-reseta-posicao"
            onClick={() => resetNavigate(center)}
          >
            <MdGpsFixed fontSize="18px" />
          </ControlItem>

          <ControlZoom map={map} />

          <ControlItem>
            <MenuTypeMap map={map} />
          </ControlItem>
        </Flex>
      )}
    />
  );
}

export default MapControl;
